import { Component, useState, useCallback, type ReactNode } from 'react';
import { Card, Typography, Button, Space, Tooltip, Popconfirm, Input } from 'antd';
import { DeleteOutlined, SettingOutlined, WarningOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useWidgetSize } from './useWidgetSize';
import { getWidget } from './widgets/widgetRegistry';
import { usePlayerOptional } from './usePlayerContext';
import { useDashboard } from './useDashboard';
import { WidgetSettingsDrawer } from './WidgetSettingsDrawer';
import type {
  DashboardWidgetConfig,
  DashboardLayoutItem,
} from './widgets/types';
import { useTheme } from '../../contexts/useTheme';

const CARD_HEADER_HEIGHT = 36;

const { Text } = Typography;

interface WidgetErrorBoundaryProps {
  widgetType: string;
  children: ReactNode;
}

interface WidgetErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class WidgetErrorBoundary extends Component<WidgetErrorBoundaryProps, WidgetErrorBoundaryState> {
  constructor(props: WidgetErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): WidgetErrorBoundaryState {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: 16,
          }}
        >
          <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />
          <Text type="warning" style={{ textAlign: 'center' }}>
            Widget "{this.props.widgetType}" encountered an error
          </Text>
          <Text type="secondary" style={{ fontSize: 11, textAlign: 'center' }}>
            {this.state.error?.message}
          </Text>
        </div>
      );
    }
    return this.props.children;
  }
}

interface WidgetFrameProps {
  widgetConfig: DashboardWidgetConfig;
  layoutItem: DashboardLayoutItem;
  isEditing: boolean;
  onDelete?: (widgetId: string) => void;
  onDragStart?: (e: React.PointerEvent) => void;
}

export const WidgetFrame = ({ widgetConfig, layoutItem, isEditing, onDelete, onDragStart }: WidgetFrameProps) => {
  const definition = getWidget(widgetConfig.type);
  const { ref, sizeInfo } = useWidgetSize(definition?.breakpoints);
  const { theme } = useTheme();
  const player = usePlayerOptional();
  const { updateWidgetConfig, scheduleAutoSave, updateWidgetTitle, updateWidgetShowTitle } = useDashboard();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleConfigChange = useCallback(
    (partial: Record<string, unknown>) => {
      updateWidgetConfig(widgetConfig.id, partial);
      if (!isEditing) {
        scheduleAutoSave();
      }
    },
    [updateWidgetConfig, widgetConfig.id, isEditing, scheduleAutoSave],
  );

  const hasSettings = !!definition?.settingsComponent;
  const hasInternalSettings = !!definition?.hasInternalSettings;
  const [internalSettingsTrigger, setInternalSettingsTrigger] = useState(0);

  const sizeClass = sizeInfo.sizeClass;

  const canSync = definition?.capabilities.canSyncToPlayer ?? false;
  const playerTimestamp = canSync ? player?.currentTimestamp ?? null : undefined;

  const displayTitle = widgetConfig.title?.trim() || definition?.title || widgetConfig.type;
  const titleFontSize = sizeClass === 'compact' ? 12 : 13;
  const showTitle = widgetConfig.showTitle !== false;
  const headerVisible = showTitle || isEditing;

  const editActions = isEditing ? (
    <Space size={4} style={{ flexShrink: 0, marginLeft: 8 }}>
      <Tooltip title={showTitle ? 'Hide title' : 'Show title'}>
        <Button
          type="text"
          size="small"
          icon={showTitle ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          style={{ fontSize: 12 }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => updateWidgetShowTitle(widgetConfig.id, !showTitle)}
        />
      </Tooltip>
      {hasSettings && (
        <Tooltip title="Widget settings">
          <Button
            type="text"
            size="small"
            icon={<SettingOutlined />}
            style={{ fontSize: 12 }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setSettingsOpen(true)}
          />
        </Tooltip>
      )}
      {!hasSettings && hasInternalSettings && (
        <Tooltip title="Widget settings">
          <Button
            type="text"
            size="small"
            icon={<SettingOutlined />}
            style={{ fontSize: 12 }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setInternalSettingsTrigger((n) => n + 1)}
          />
        </Tooltip>
      )}
      <Popconfirm
        title="Remove this widget?"
        onConfirm={() => onDelete?.(widgetConfig.id)}
        okText="Remove"
        cancelText="Cancel"
      >
        <Tooltip title="Remove widget">
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            style={{ fontSize: 12 }}
            onPointerDown={(e) => e.stopPropagation()}
          />
        </Tooltip>
      </Popconfirm>
    </Space>
  ) : null;

  const titleContent = headerVisible ? (
    <div
      onPointerDown={isEditing ? onDragStart : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minHeight: 22,
        touchAction: isEditing ? 'none' : undefined,
      }}
    >
      {isEditing ? (
        showTitle ? (
          <Input
            value={widgetConfig.title}
            onChange={(e) => updateWidgetTitle(widgetConfig.id, e.target.value)}
            onPointerDown={(e) => e.stopPropagation()}
            size="small"
            maxLength={100}
            placeholder={definition?.title ?? 'Widget title'}
            style={{
              flex: 1,
              fontSize: titleFontSize,
              fontWeight: 600,
              padding: '0 4px',
            }}
          />
        ) : (
          <Text type="secondary" style={{ flex: 1, fontSize: titleFontSize, fontStyle: 'italic' }}>
            Title hidden
          </Text>
        )
      ) : (
        <Text
          strong
          ellipsis
          style={{
            fontSize: titleFontSize,
            flex: 1,
          }}
        >
          {displayTitle}
        </Text>
      )}
      {editActions}
    </div>
  ) : undefined;

  const cardStyles = {
    header: headerVisible
      ? {
          minHeight: CARD_HEADER_HEIGHT,
          padding: '0 12px',
          cursor: isEditing ? 'grab' : 'default',
          touchAction: isEditing ? 'none' : undefined,
        }
      : undefined,
    body: {
      height: headerVisible ? `calc(100% - ${CARD_HEADER_HEIGHT}px)` : '100%',
      padding: 0,
      overflow: 'hidden' as const,
    },
  };

  if (!definition) {
    return (
      <Card
        size="small"
        title={titleContent}
        style={{ height: '100%', overflow: 'hidden' }}
        styles={cardStyles}
      >
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />
          <Text type="secondary">Unknown widget type: "{widgetConfig.type}"</Text>
        </div>
      </Card>
    );
  }

  const WidgetComponent = definition.component;

  return (
    <Card
      size="small"
      title={titleContent}
      style={{
        height: '100%',
        overflow: 'hidden',
        borderColor: isEditing
          ? theme === 'dark'
            ? 'rgba(24, 144, 255, 0.3)'
            : 'rgba(24, 144, 255, 0.2)'
          : undefined,
      }}
      styles={cardStyles}
    >
      <div
        ref={ref}
        style={{
          height: '100%',
          width: '100%',
          overflow: definition.bodyOverflow ?? (definition.category === 'map' ? 'hidden' : 'auto'),
        }}
      >
        <WidgetErrorBoundary widgetType={widgetConfig.type}>
          <WidgetComponent
            config={widgetConfig.config}
            onConfigChange={handleConfigChange}
            openSettingsTrigger={hasInternalSettings ? internalSettingsTrigger : undefined}
            size={{ width: layoutItem.width, height: layoutItem.height }}
            pixelSize={sizeInfo.pixelSize}
            sizeClass={sizeClass}
            isEditing={isEditing}
            playerTimestamp={playerTimestamp}
          />
        </WidgetErrorBoundary>
      </div>

      {hasSettings && (
        <WidgetSettingsDrawer
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          widgetConfig={widgetConfig}
          layoutItem={layoutItem}
        />
      )}
    </Card>
  );
};
